import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Brain,
  Clapperboard,
  Download,
  Home,
  Printer,
  Loader2,
  Sparkles,
  Star,
  Trophy,
} from 'lucide-react';
import PageSeo from '@/seo/PageSeo';
import GoogleAnalytics from '@/components/GoogleAnalytics';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  getAssessmentIntro,
  getAssessmentQuestions,
  getLevelTransitionAfterQuestionId,
  hiQuestionPackLooksUntranslated,
} from '@/data/assessmentPack';
import { getApiBase } from '@/lib/apiBase';
import { parseAssessmentGradeFromText } from '@/lib/parseAssessmentGrade';
import {
  fillCopy,
  friendlySurfaceErrorForLocale,
  getAssessmentPageCopy,
  validateReportEmailClient,
} from '@/data/assessmentPageCopy';
import { clearFlowStorage, getFlowOverride, getSessionToken, setSessionToken } from '@/lib/flowStorage';
import { logoutSession } from '@/lib/sessionLogout';
import { resolveAutoDownloadPdfAfterGenerate, resolveServerPdfDownloadEnabled } from '@/lib/featureFlags';
import { isUsableReportPrintHtml, printFullDocumentHtml } from '@/lib/printFullDocumentHtml';
import { trackEvent } from '@/lib/trackEvent';

const API_BASE = getApiBase();

/** Local resume of in-progress answers (same tab) if the tab closes mid-assessment. */
const ASSESSMENT_PROGRESS_KEY = 'crc_assessment_progress_v1';

function readStoredAssessmentProgress() {
  try {
    const raw = sessionStorage.getItem(ASSESSMENT_PROGRESS_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (!o || typeof o !== 'object' || o.sessionId == null || !Array.isArray(o.answers) || o.answers.length !== 34) {
      return null;
    }
    return o;
  } catch {
    return null;
  }
}

function writeStoredAssessmentProgress(sessionId, qIndex, answers) {
  try {
    if (!sessionId || !Array.isArray(answers) || answers.length !== 34) return;
    sessionStorage.setItem(
      ASSESSMENT_PROGRESS_KEY,
      JSON.stringify({ sessionId, qIndex, answers, t: Date.now() }),
    );
  } catch {
    /* ignore */
  }
}

function clearStoredAssessmentProgress() {
  try {
    sessionStorage.removeItem(ASSESSMENT_PROGRESS_KEY);
  } catch {
    /* ignore */
  }
}

function formatElapsedMmSs(totalSec) {
  const sec = Math.max(0, Math.floor(Number(totalSec) || 0));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Avoid infinite "Saving…" if the API or DB never answers (common on bad MySQL routes). */
function fetchWithTimeout(url, init = {}, timeoutMs = 45_000) {
  const ms = Math.max(8000, Number(timeoutMs) || 45_000);
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return fetch(url, { ...init, signal: AbortSignal.timeout(ms) });
  }
  const ac = new AbortController();
  const id = window.setTimeout(() => ac.abort(), ms);
  return fetch(url, { ...init, signal: ac.signal }).finally(() => window.clearTimeout(id));
}

/**
 * Older API builds used a hint regex that matched IPv4 too, so they returned an "IPv6 client" hint
 * even when the error shows @'w.x.y.z'. Prefer a clear message for that case.
 */
function formatDatabasePingDetail(ping, fallback) {
  const hint = ping?.hint != null ? String(ping.hint) : '';
  const error = ping?.error != null ? String(ping.error) : '';
  const ipv4Client = /@'(?:\d{1,3}\.){3}\d{1,3}'/i.test(error);
  if (ipv4Client && /ipv6/i.test(hint)) {
    const parts = [
      'Our system could not complete a secure database connection from this server. If you manage this site, ask your hosting provider to allow database access from this server’s IP and confirm your database login details match your hosting panel.',
      error,
    ].filter(Boolean);
    return parts.join(' ') || fallback;
  }
  return [hint, error].filter(Boolean).join(' ') || fallback;
}

const PHASE = {
  boot: 'boot',
  intro: 'intro',
  question: 'question',
  bridge: 'bridge',
  plan: 'plan',
  unlock: 'unlock',
  parent: 'parent',
  loading: 'loading',
  result: 'result',
  /** Magic-link / ?token= mismatch or invalid — avoid redirect loops with a clear screen. */
  sessionLinkError: 'sessionLinkError',
};

function loadRazorpayScript() {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Could not load Razorpay checkout.'));
    document.body.appendChild(s);
  });
}

function defaultPdfFileName() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `CareerNova_Report_${y}${m}${day}.pdf`;
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || defaultPdfFileName();
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 4000);
}

function downloadPdfFromBase64(b64, fileName) {
  try {
    const bin = atob(String(b64 || '').replace(/\s/g, ''));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    downloadBlob(new Blob([bytes], { type: 'application/pdf' }), fileName);
  } catch {
    /* ignore */
  }
}

function topTraitKeysFromProfile(profile) {
  if (!profile || typeof profile !== 'object') return [];
  const keys = ['analytical', 'creative', 'structured', 'social', 'practical', 'risk'];
  return [...keys]
    .sort((a, b) => (Number(profile[b]) || 0) - (Number(profile[a]) || 0))
    .slice(0, 2);
}

/** Surface HTTP / JSON body from lead save (shown under the intro form for debugging). */
function formatLeadSaveHttpError(res, bodyText, json) {
  const status = res?.status ?? 0;
  const fromJson = json?.error || json?.message;
  const snippet = String(bodyText || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 500);
  if (fromJson) return `${fromJson} (HTTP ${status})`;
  if (snippet) return `HTTP ${status}: ${snippet}`;
  return `Request failed (HTTP ${status})`;
}

const backdrops = [
  'bg-gradient-to-br from-[#0a0620] via-[#1a1040] to-[#0f172a]',
  'bg-gradient-to-br from-[#0c1222] via-[#162032] to-[#0f172a]',
  'bg-gradient-to-br from-[#0f1724] via-[#134e4a] to-[#0f172a]',
  'bg-gradient-to-br from-[#1c1008] via-[#292524] to-[#0f172a]',
  'bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0c4a6e]',
];

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const fn = () => setReduced(mq.matches);
    fn();
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return reduced;
}

export default function AssessmentPage() {
  const reducedMotion = usePrefersReducedMotion();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const flowOverride = useMemo(() => {
    const q = searchParams.get('flow');
    if (q === 'direct' || q === 'school') return q;
    return getFlowOverride();
  }, [searchParams]);

  useEffect(() => {
    const t = searchParams.get('token');
    if (t) setSessionToken(t);
  }, [searchParams]);
  const [phase, setPhase] = useState(PHASE.boot);
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState(() => Array(34).fill(''));
  const [pendingAnswers, setPendingAnswers] = useState(null);
  const [bridgeText, setBridgeText] = useState('');
  const [name, setName] = useState('');
  /** Required for production; stored server-side with analytics for fulfilment / future email delivery. */
  const [reportEmail, setReportEmail] = useState('');
  const [introFieldError, setIntroFieldError] = useState('');
  const [pdfBase64, setPdfBase64] = useState('');
  /** When set, PDF bytes live on the API disk; download via POST /api/reports/download. */
  const [pdfDownload, setPdfDownload] = useState(null);
  const pdfDownloadRef = useRef(null);
  /** Server has a full-report print document (fetch GET /api/assessment/report-print-html on Print). */
  const [reportPrintAvailable, setReportPrintAvailable] = useState(false);
  const [pdfWarning, setPdfWarning] = useState('');
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  /** True while re-requesting report+PDF from the result screen (keeps button visible). */
  const [reportBusy, setReportBusy] = useState(false);
  /** Full-report print path: modal-style status so the page does not flicker before the system print dialog. */
  const [printSheetOpen, setPrintSheetOpen] = useState(false);
  /** After a failed on-result report retry, surface errors again even in print-first mode. */
  const [reportSoftFail, setReportSoftFail] = useState(false);
  /** Server-PDF flag on: brief state while auto-download runs after generation. */
  const [autoFinishingReport, setAutoFinishingReport] = useState(false);
  const [publicConfig, setPublicConfig] = useState({
    unlock: 'none',
    razorpayKeyId: '',
    reportPricePaise: 129900,
    currency: 'INR',
    requireReportEmail: true,
    funnelMode: 'legacy',
    plans: [],
    databaseConfigured: false,
    organization: null,
    defaultAssessmentLocale: 'en',
    features: {},
  });
  const [assessmentLocale, setAssessmentLocale] = useState(() => {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('lang');
      if (fromUrl) return String(fromUrl).toLowerCase().startsWith('hi') ? 'hi' : 'en';
      const saved = window.localStorage.getItem('crc_assessment_lang');
      if (saved === 'hi' || saved === 'en') return saved;
    } catch {
      /* ignore */
    }
    return 'en';
  });
  const [sessionId, setSessionId] = useState('');
  /** Normalized email stored on the lead for this session (must match POST /api/assessment/report). */
  const [sessionLeadEmail, setSessionLeadEmail] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadClass, setLeadClass] = useState('');
  /** Question pack class year (9–12); server stores on funnel session for analytics. */
  const [assessmentGrade, setAssessmentGrade] = useState(10);
  const [leadConsent, setLeadConsent] = useState(false);
  const [leadBusy, setLeadBusy] = useState(false);
  /** Before POST /api/leads: real MySQL ping via /api/health?db=1 (plain /api/health does not touch the DB). */
  const [leadDbCheck, setLeadDbCheck] = useState('ok');
  const [leadDbDetail, setLeadDbDetail] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planBusy, setPlanBusy] = useState(false);
  const [unlockVoucher, setUnlockVoucher] = useState('');
  const [unlockVoucherMsg, setUnlockVoucherMsg] = useState('');
  const [unlockBusy, setUnlockBusy] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [rzpDone, setRzpDone] = useState(null);
  const [parentPhone, setParentPhone] = useState('');
  const [parentBusy, setParentBusy] = useState(false);
  const [parentErr, setParentErr] = useState('');
  const [parentPreviewProfile, setParentPreviewProfile] = useState(null);
  const [reportPreviewUrl, setReportPreviewUrl] = useState('');
  /** From POST /api/users/add-parent — Twilio SID or mock mode for debugging delivery. */
  const [parentNotifyMeta, setParentNotifyMeta] = useState(null);
  const unlockSnapshotRef = useRef({});
  /** Debounced write of in-progress answers to sessionStorage (resume after refresh). */
  const assessmentProgressSaveTimerRef = useRef(null);
  /** Answers used for the last / last successful report request (reliable PDF retry). */
  const lastAnswersRef = useRef(null);
  const parentElapsedIntervalRef = useRef(null);
  const parentWaitStartedAtRef = useRef(0);
  /** Elapsed seconds while parent report + notify is in flight; null when idle */
  const [parentElapsedSeconds, setParentElapsedSeconds] = useState(null);
  /** When server PDF download is enabled, set after successful generation to trigger one auto-download on the result screen. */
  const autoDownloadAndExitRef = useRef(false);

  const questions = useMemo(
    () => getAssessmentQuestions(assessmentLocale, assessmentGrade),
    [assessmentLocale, assessmentGrade],
  );
  const assessmentIntro = useMemo(() => getAssessmentIntro(assessmentLocale), [assessmentLocale]);
  const copy = useMemo(() => getAssessmentPageCopy(assessmentLocale), [assessmentLocale]);
  const q = questions[qIndex];
  const progressPct = useMemo(() => (answers.filter(Boolean).length / 34) * 100, [answers]);
  /** Single start screen: first visit to paid funnel must POST /api/leads before questions. */
  const needsLeadCreate = useMemo(
    () =>
      publicConfig.funnelMode === 'lead_plan_pay' &&
      !sessionId &&
      flowOverride !== 'direct' &&
      flowOverride !== 'school',
    [publicConfig.funnelMode, sessionId, flowOverride],
  );
  /** Lead funnel: email is bound to MySQL lead row — do not allow edits after session exists. */
  const introEmailLocked =
    Boolean(sessionId) &&
    (publicConfig.funnelMode === 'lead_plan_pay' ||
      flowOverride === 'direct' ||
      flowOverride === 'school');

  /**
   * True when this browser has a funnel session (paid direct / school) even if /api/public-config
   * has not finished loading yet (funnelMode would still be default "legacy" until then).
   */
  const isLeadFunnelBrowserSession = useMemo(
    () =>
      Boolean(sessionId) &&
      (publicConfig.funnelMode === 'lead_plan_pay' ||
        flowOverride === 'direct' ||
        flowOverride === 'school'),
    [sessionId, publicConfig.funnelMode, flowOverride],
  );

  /** Server `paymentStatus` for leave/refund messaging (direct pay-first and post-unlock pay). */
  const [funnelPaymentStatus, setFunnelPaymentStatus] = useState('');
  /** School: `org_prepaid` when the org pays for student reports; otherwise parent pays before full print/PDF. */
  const [schoolReportCoverage, setSchoolReportCoverage] = useState('parent_pays');
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  /** `home` → navigate `/`; `restart` → run restart() (e.g. unlock Cancel). */
  const [leaveAction, setLeaveAction] = useState(null);

  useEffect(() => {
    if (rzpDone?.razorpayPaymentId) setFunnelPaymentStatus('paid');
  }, [rzpDone]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const urlTokenEarly = searchParams.get('token')?.trim() || '';
        if (urlTokenEarly) setSessionToken(urlTokenEarly);

        let org = '';
        try {
          org = new URLSearchParams(window.location.search).get('org') || new URLSearchParams(window.location.search).get('orgSlug') || '';
        } catch {
          /* ignore */
        }
        const cfgUrl =
          org.trim() !== ''
            ? `${API_BASE}/api/public-config?${new URLSearchParams({ orgSlug: org.trim() }).toString()}`
            : `${API_BASE}/api/public-config`;
        const cfgMs = Number(process.env.REACT_APP_PUBLIC_CONFIG_TIMEOUT_MS || 25000);
        const res = await fetchWithTimeout(cfgUrl, {}, cfgMs);
        const data = await res.json().catch(() => ({}));
        if (!cancelled && data && typeof data.unlock === 'string') {
          const defLoc = data.defaultAssessmentLocale === 'hi' ? 'hi' : 'en';
          setPublicConfig((prev) => ({
            ...prev,
            ...data,
            requireReportEmail: data.requireReportEmail !== false,
            funnelMode: data.funnelMode || 'legacy',
            plans: Array.isArray(data.plans) ? data.plans : [],
            databaseConfigured: Boolean(data.databaseConfigured),
            organization: data.organization || null,
            defaultAssessmentLocale: defLoc,
          }));
          try {
            const fromUrl = new URLSearchParams(window.location.search).get('lang');
            if (!fromUrl && !window.localStorage.getItem('crc_assessment_lang')) {
              setAssessmentLocale(defLoc);
            }
          } catch {
            /* ignore */
          }
        }
        if (cancelled) return;
        if (data.funnelMode === 'lead_plan_pay') {
          const fo = flowOverride;
          if (fo !== 'school' && fo !== 'direct') {
            try {
              sessionStorage.removeItem('crc_session_id');
              clearFlowStorage();
            } catch {
              /* ignore */
            }
            if (!cancelled) navigate('/start', { replace: true });
            return;
          }
          let sid = '';
          try {
            sid = sessionStorage.getItem('crc_session_id') || '';
          } catch {
            /* ignore */
          }
          const urlToken = searchParams.get('token')?.trim() || '';
          if (urlToken && (flowOverride === 'direct' || flowOverride === 'school')) {
            const tr = await fetchWithTimeout(
              `${API_BASE}/api/public/session-from-token?token=${encodeURIComponent(urlToken)}`,
              {},
              cfgMs,
            );
            const td = await tr.json().catch(() => ({}));
            if (!tr.ok) {
              if (!cancelled) setPhase(PHASE.sessionLinkError);
              return;
            }
            sid = String(td.sessionId || '').trim();
            if (!sid) {
              if (!cancelled) setPhase(PHASE.sessionLinkError);
              return;
            }
            try {
              sessionStorage.setItem('crc_session_id', sid);
            } catch {
              /* ignore */
            }
          }
          const tok = getSessionToken();
          if (sid && flowOverride === 'direct' && !tok) {
            navigate('/start', { replace: true });
            return;
          }
          if (sid && tok && flowOverride === 'direct') {
            const ar = await fetchWithTimeout(`${API_BASE}/api/sessions/${sid}/access`, {
              headers: { 'X-Session-Token': tok },
            });
            const ad = await ar.json().catch(() => ({}));
            if (!cancelled && (!ar.ok || !ad.canAccessAssessment)) {
              const payTok = String(tok || getSessionToken() || '').trim();
              navigate(
                payTok
                  ? `/payment?token=${encodeURIComponent(payTok)}`
                  : '/payment',
                { replace: true },
              );
              return;
            }
          }
          if (sid) {
            const sr = await fetchWithTimeout(`${API_BASE}/api/sessions/${sid}`, {}, 20000);
            if (sr.ok) {
              const sd = await sr.json().catch(() => ({}));
              if (!cancelled && sd.sessionId) {
                setSessionId(sd.sessionId);
                setName(String(sd.leadName || ''));
                const restoredEmail = String(sd.email || '').trim().toLowerCase();
                setReportEmail(restoredEmail);
                setSessionLeadEmail(restoredEmail);
                setFunnelPaymentStatus(String(sd.paymentStatus || 'pending').toLowerCase());
                if (String(sd.source || '').toLowerCase() === 'school' && sd.schoolReportCoverage) {
                  setSchoolReportCoverage(String(sd.schoolReportCoverage));
                }
                const g = Number(sd.assessmentGrade);
                setAssessmentGrade([9, 10, 11, 12].includes(g) ? g : 10);
                setLeadConsent(true);
                const draft = readStoredAssessmentProgress();
                if (draft && draft.sessionId === sd.sessionId && Array.isArray(draft.answers)) {
                  const filled = draft.answers.filter(Boolean).length;
                  if (filled > 0) {
                    setAnswers(draft.answers);
                    const maxQI = 33;
                    const qi = Math.min(maxQI, Math.max(0, Number(draft.qIndex) || 0));
                    setQIndex(qi);
                  }
                }
                // Do not stomp phase if user is already past questions (e.g. result) — effect can re-run
                // when `navigate` identity changes or on remount; forcing question re-triggers submitReport.
                if (flowOverride === 'direct' || flowOverride === 'school') {
                  setPhase((p) => (p === PHASE.boot || p === PHASE.intro ? PHASE.question : p));
                } else {
                  setPhase((p) => (p === PHASE.boot || p === PHASE.intro ? PHASE.intro : p));
                }
                return;
              }
            }
            try {
              sessionStorage.removeItem('crc_session_id');
            } catch {
              /* ignore */
            }
          }
          if (!cancelled) {
            setPhase((p) => (p === PHASE.boot || p === PHASE.intro ? PHASE.intro : p));
          }
        } else if (!cancelled) {
          setPhase((p) => (p === PHASE.boot || p === PHASE.intro ? PHASE.intro : p));
        }
      } catch {
        if (!cancelled) {
          setPhase((p) => (p === PHASE.boot || p === PHASE.intro ? PHASE.intro : p));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [flowOverride, navigate, searchParams]);

  const assessmentStartLogged = useRef(false);
  useEffect(() => {
    if (phase !== PHASE.question || !sessionId) return;
    if (assessmentStartLogged.current) return;
    assessmentStartLogged.current = true;
    void trackEvent('assessment_started', {
      sessionId,
      meta: { grade: assessmentGrade },
    });
  }, [phase, sessionId, assessmentGrade]);

  const assessmentDroppedLogged = useRef(false);
  useEffect(() => {
    const onPageHide = () => {
      if (assessmentDroppedLogged.current || !sessionId) return;
      if (phase !== PHASE.question && phase !== PHASE.bridge) return;
      assessmentDroppedLogged.current = true;
      void trackEvent('assessment_dropped', { sessionId, meta: { phase } });
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [phase, sessionId]);

  useEffect(() => {
    if (!isLeadFunnelBrowserSession || !sessionId) return;
    if (phase !== PHASE.question && phase !== PHASE.bridge) return;
    if (assessmentProgressSaveTimerRef.current) clearTimeout(assessmentProgressSaveTimerRef.current);
    assessmentProgressSaveTimerRef.current = setTimeout(() => {
      writeStoredAssessmentProgress(sessionId, qIndex, answers);
    }, 500);
    return () => {
      if (assessmentProgressSaveTimerRef.current) clearTimeout(assessmentProgressSaveTimerRef.current);
    };
  }, [isLeadFunnelBrowserSession, sessionId, phase, qIndex, answers]);

  useEffect(() => {
    const l = searchParams.get('lang');
    if (!l) return;
    const next = l.toLowerCase().startsWith('hi') ? 'hi' : 'en';
    setAssessmentLocale(next);
    try {
      window.localStorage.setItem('crc_assessment_lang', next);
    } catch {
      /* ignore */
    }
  }, [searchParams]);

  useEffect(() => {
    try {
      document.documentElement.lang = assessmentLocale === 'hi' ? 'hi' : 'en';
    } catch {
      /* ignore */
    }
  }, [assessmentLocale]);

  useEffect(() => {
    return () => {
      if (parentElapsedIntervalRef.current) {
        clearInterval(parentElapsedIntervalRef.current);
        parentElapsedIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const needPing =
      publicConfig.funnelMode === 'lead_plan_pay' && !sessionId && publicConfig.databaseConfigured;
    if (!needPing) {
      setLeadDbCheck('ok');
      setLeadDbDetail('');
      return;
    }
    let cancelled = false;
    setLeadDbCheck('checking');
    setLeadDbDetail('');
    (async () => {
      try {
        const res = await fetchWithTimeout(`${API_BASE}/api/health?db=1`, {}, 15000);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        const ping = data.databasePing;
        if (ping && ping.ok === true) {
          setLeadDbCheck('ok');
          setLeadDbDetail('');
        } else if (ping && ping.ok === false) {
          setLeadDbCheck('error');
          const c = getAssessmentPageCopy(assessmentLocale);
          setLeadDbDetail(formatDatabasePingDetail(ping, c.dbPingSaveHang));
        } else {
          const c = getAssessmentPageCopy(assessmentLocale);
          setLeadDbCheck('error');
          setLeadDbDetail(
            ping
              ? formatDatabasePingDetail(ping, c.dbPingSaveHang)
              : `${c.apiVerifyDbFail} ${c.dbPingSaveHang}`.trim(),
          );
        }
      } catch (e) {
        if (!cancelled) {
          const c = getAssessmentPageCopy(assessmentLocale);
          setLeadDbCheck('error');
          setLeadDbDetail(
            friendlySurfaceErrorForLocale(String(e?.message || e), assessmentLocale) || c.apiVerifyDbFail,
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [publicConfig.funnelMode, publicConfig.databaseConfigured, sessionId, assessmentLocale]);

  const submitReport = useCallback(async (finalAnswers, opts = {}) => {
    const stayOnResult = Boolean(opts.stayOnResult);
    if (Array.isArray(finalAnswers) && finalAnswers.length) {
      lastAnswersRef.current = finalAnswers;
    }
    const meta = stayOnResult ? unlockSnapshotRef.current : {};
    const emailRequired = publicConfig.requireReportEmail !== false;
    const boundLead =
      isLeadFunnelBrowserSession && sessionLeadEmail
        ? String(sessionLeadEmail).trim().toLowerCase()
        : '';
    const re = String(
      boundLead || (opts.reportEmail ?? meta.reportEmail ?? opts.email ?? meta.email ?? reportEmail),
    )
      .trim()
      .toLowerCase();
    const emailProblem = validateReportEmailClient(re, emailRequired, assessmentLocale);
    if (emailProblem) {
      setError(emailProblem);
      setReportSoftFail(true);
      if (!stayOnResult) setPhase(PHASE.intro);
      else setPhase(PHASE.result);
      if (stayOnResult) setReportBusy(false);
      return;
    }
    const body = {
      name: name.trim() || 'Student',
      answers: finalAnswers,
      reportEmail: re,
      email: re,
      voucherCode: opts.voucherCode ?? meta.voucherCode ?? '',
      razorpayOrderId: opts.razorpayOrderId ?? meta.razorpayOrderId ?? '',
      razorpayPaymentId: opts.razorpayPaymentId ?? meta.razorpayPaymentId ?? '',
      razorpaySignature: opts.razorpaySignature ?? meta.razorpaySignature ?? '',
      ...(isLeadFunnelBrowserSession ? { sessionId } : {}),
    };
    setError('');
    if (!stayOnResult) {
      setPdfBase64('');
      setPdfDownload(null);
      pdfDownloadRef.current = null;
      setReportPrintAvailable(false);
      setPdfWarning('');
      setProfile(null);
      setPhase(PHASE.loading);
    } else {
      setReportBusy(true);
      setReportSoftFail(false);
      setPdfBase64('');
      setPdfDownload(null);
      pdfDownloadRef.current = null;
      setReportPrintAvailable(false);
      // Keep prior pdfWarning until we get a new response so the user is not left with a blank screen.
    }
    /** LLM + Puppeteer can exceed 4m on small VPS; mobile networks need longer download for large JSON. Use fetchWithTimeout so older iOS gets AbortController fallback, not only AbortSignal.timeout. */
    const fetchMs = Number(process.env.REACT_APP_REPORT_FETCH_TIMEOUT_MS || 360000);
    try {
      const res = await fetchWithTimeout(
        `${API_BASE}/api/assessment/report`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
        fetchMs,
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Request failed (${res.status})`);
      }
      clearStoredAssessmentProgress();
      setProfile(data.profile);
      setReportPrintAvailable(Boolean(data.reportPrintAvailable));
      setPdfBase64(data.pdfBase64 || '');
      const nextDl = data.pdfDownload?.reportId && data.pdfDownload?.token ? data.pdfDownload : null;
      pdfDownloadRef.current = nextDl;
      setPdfDownload(nextDl);
      setPdfWarning(data.pdfWarning ? friendlySurfaceErrorForLocale(data.pdfWarning, assessmentLocale) : '');
      unlockSnapshotRef.current = {
        ...unlockSnapshotRef.current,
        reportEmail: re,
        email: re,
      };
      setReportSoftFail(false);
      if (
        resolveServerPdfDownloadEnabled(publicConfig) &&
        resolveAutoDownloadPdfAfterGenerate(publicConfig) &&
        (data.pdfBase64 || nextDl)
      ) {
        autoDownloadAndExitRef.current = true;
      }
      setPhase(PHASE.result);
    } catch (e) {
      const aborted = e?.name === 'AbortError' || e?.name === 'TimeoutError';
      setReportSoftFail(true);
      setError(
        aborted
          ? fillCopy(getAssessmentPageCopy(assessmentLocale).reportStepTimeout, {
              seconds: Math.round(fetchMs / 1000),
            })
          : friendlySurfaceErrorForLocale(String(e.message || e), assessmentLocale),
      );
      setPhase(PHASE.result);
    } finally {
      if (stayOnResult) setReportBusy(false);
    }
  }, [
    name,
    reportEmail,
    sessionLeadEmail,
    publicConfig.requireReportEmail,
    publicConfig,
    sessionId,
    assessmentGrade,
    assessmentLocale,
    isLeadFunnelBrowserSession,
  ]);

  const pick = useCallback(
    (letter) => {
      const next = [...answers];
      next[qIndex] = letter;
      setAnswers(next);
      const transition = getLevelTransitionAfterQuestionId(assessmentLocale, q.id);
      if (transition && qIndex < questions.length - 1) {
        setBridgeText(transition);
        setPhase(PHASE.bridge);
      } else if (qIndex >= questions.length - 1) {
        setPendingAnswers(next);
        setAnswers(next);
        if (isLeadFunnelBrowserSession) {
          void (async () => {
            try {
              const tok = getSessionToken();
              await fetch(`${API_BASE}/api/sessions/${sessionId}/snapshot`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(tok ? { 'X-Session-Token': tok } : {}),
                },
                body: JSON.stringify({ answers: next }),
              });
            } catch {
              /* non-fatal */
            }
            clearStoredAssessmentProgress();
            if (flowOverride === 'school') {
              if (schoolReportCoverage === 'org_prepaid') {
                setPhase(PHASE.loading);
                void submitReport(next, {});
                return;
              }
              setPhase(PHASE.parent);
              return;
            }
            if (flowOverride === 'direct') {
              setPhase(PHASE.loading);
              void submitReport(next, {});
              return;
            }
            setPhase(PHASE.plan);
          })();
        } else if (publicConfig.unlock === 'none') {
          setPhase(PHASE.loading);
          void submitReport(next);
        } else {
          setPhase(PHASE.unlock);
        }
      } else {
        setQIndex((i) => i + 1);
      }
    },
    [
      answers,
      q?.id,
      qIndex,
      questions.length,
      publicConfig.unlock,
      publicConfig.funnelMode,
      sessionId,
      submitReport,
      assessmentLocale,
      flowOverride,
      isLeadFunnelBrowserSession,
      schoolReportCoverage,
    ],
  );

  const continueAfterBridge = useCallback(() => {
    setQIndex((i) => i + 1);
    setPhase(PHASE.question);
    setBridgeText('');
  }, []);

  const logoutCurrentSession = useCallback(async () => {
    await logoutSession({
      apiBase: API_BASE,
      flow: flowOverride,
      sessionId,
      sessionToken: getSessionToken(),
    });
  }, [flowOverride, sessionId]);

  const restart = useCallback(async () => {
    await logoutCurrentSession();
    clearStoredAssessmentProgress();
    setSessionId('');
    setSessionLeadEmail('');
    setSelectedPlan(null);
    setLeadPhone('');
    setLeadClass('');
    setAssessmentGrade(10);
    setLeadConsent(false);
    setPhase(PHASE.intro);
    setQIndex(0);
    setAnswers(Array(34).fill(''));
    setPendingAnswers(null);
    setBridgeText('');
    setName('');
    setReportEmail('');
    setIntroFieldError('');
    setPdfBase64('');
    setPdfDownload(null);
    pdfDownloadRef.current = null;
    setReportPrintAvailable(false);
    setPdfWarning('');
    setProfile(null);
    setError('');
    setReportBusy(false);
    setPrintSheetOpen(false);
    setReportSoftFail(false);
    setAutoFinishingReport(false);
    autoDownloadAndExitRef.current = false;
    setUnlockVoucher('');
    setUnlockVoucherMsg('');
    setRzpDone(null);
    setFunnelPaymentStatus('');
    setParentPhone('');
    setParentErr('');
    setParentPreviewProfile(null);
    setReportPreviewUrl('');
    setParentNotifyMeta(null);
    setParentElapsedSeconds(null);
    parentWaitStartedAtRef.current = 0;
    unlockSnapshotRef.current = {};
    lastAnswersRef.current = null;
    setSchoolReportCoverage('parent_pays');
  }, [logoutCurrentSession]);

  const shouldConfirmLeaveAssessment = useMemo(() => {
    if (!isLeadFunnelBrowserSession || !sessionId) return false;
    if (![PHASE.question, PHASE.bridge, PHASE.unlock].includes(phase)) return false;
    if (flowOverride === 'school') return true;
    if (flowOverride === 'direct') {
      const paid = funnelPaymentStatus === 'paid' || Boolean(rzpDone?.razorpayPaymentId);
      return paid;
    }
    return false;
  }, [isLeadFunnelBrowserSession, sessionId, phase, flowOverride, funnelPaymentStatus, rzpDone]);

  /** School + parent-pay: full server print/PDF only after payment; org-prepaid and direct always allow print when shown. */
  const schoolStudentCanPrintFullReport = useMemo(() => {
    if (flowOverride !== 'school') return true;
    if (schoolReportCoverage === 'org_prepaid') return true;
    const paid =
      String(funnelPaymentStatus || '').toLowerCase() === 'paid' || Boolean(rzpDone?.razorpayPaymentId);
    return paid;
  }, [flowOverride, schoolReportCoverage, funnelPaymentStatus, rzpDone?.razorpayPaymentId]);

  useEffect(() => {
    if (phase !== PHASE.result) return;
    if (flowOverride !== 'school') return;
    if (schoolReportCoverage !== 'parent_pays') return;
    if (String(funnelPaymentStatus || '').toLowerCase() === 'paid') return;
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const sr = await fetchWithTimeout(`${API_BASE}/api/sessions/${encodeURIComponent(sessionId)}`, {}, 15000);
        if (!sr.ok || cancelled) return;
        const sd = await sr.json().catch(() => ({}));
        if (cancelled || !sd.sessionId) return;
        if (sd.paymentStatus != null) setFunnelPaymentStatus(String(sd.paymentStatus).toLowerCase());
        const cov = String(sd.schoolReportCoverage || '').trim();
        if (cov === 'org_prepaid' || cov === 'parent_pays') setSchoolReportCoverage(cov);
      } catch {
        /* ignore */
      }
    };
    void poll();
    const id = window.setInterval(() => void poll(), 12000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [phase, flowOverride, schoolReportCoverage, funnelPaymentStatus, sessionId]);

  const openLeaveConfirm = useCallback(
    (action) => {
      if (!shouldConfirmLeaveAssessment) {
        if (action === 'home') {
          void (async () => {
            await logoutCurrentSession();
            clearStoredAssessmentProgress();
            navigate('/');
          })();
        } else {
          void restart();
        }
        return;
      }
      setLeaveAction(action);
      setLeaveDialogOpen(true);
    },
    [shouldConfirmLeaveAssessment, navigate, restart, logoutCurrentSession],
  );

  const confirmLeaveAssessment = useCallback(async () => {
    const a = leaveAction;
    setLeaveDialogOpen(false);
    setLeaveAction(null);
    if (a === 'home') {
      await logoutCurrentSession();
      clearStoredAssessmentProgress();
      navigate('/');
    } else if (a === 'restart') {
      await restart();
    }
  }, [leaveAction, navigate, restart, logoutCurrentSession]);

  const submitLead = useCallback(async () => {
    const c = getAssessmentPageCopy(assessmentLocale);
    setIntroFieldError('');
    if (
      publicConfig.funnelMode === 'lead_plan_pay' &&
      publicConfig.databaseConfigured &&
      leadDbCheck !== 'ok'
    ) {
      setIntroFieldError(leadDbCheck === 'checking' ? c.errDbWait : leadDbDetail || c.errDbBlock);
      return;
    }
    const pe = validateReportEmailClient(reportEmail, true, assessmentLocale);
    if (pe) {
      setIntroFieldError(pe);
      return;
    }
    if (!name.trim()) {
      setIntroFieldError(c.errName);
      return;
    }
    if (!leadConsent) {
      setIntroFieldError(c.errConsent);
      return;
    }
    setLeadBusy(true);
    const leadFetchMs = Number(process.env.REACT_APP_LEAD_FETCH_TIMEOUT_MS || 45000);
    const orgSlug =
      (searchParams.get('org') || searchParams.get('orgSlug') || '').trim() || undefined;
    try {
      const res = await fetchWithTimeout(
        `${API_BASE}/api/leads`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: name.trim(),
            email: reportEmail.trim().toLowerCase(),
            phone: leadPhone.trim(),
            classGrade: leadClass.trim(),
            assessmentGrade,
            consentPersonalization: leadConsent,
            organizationSlug: orgSlug,
            utm_source: searchParams.get('utm_source') || undefined,
            utm_medium: searchParams.get('utm_medium') || undefined,
            utm_campaign: searchParams.get('utm_campaign') || undefined,
          }),
        },
        leadFetchMs,
      );
      const bodyText = await res.text();
      let data = {};
      try {
        data = bodyText ? JSON.parse(bodyText) : {};
      } catch {
        data = {};
      }
      if (!res.ok) {
        const msg = formatLeadSaveHttpError(res, bodyText, data);
        // eslint-disable-next-line no-console
        console.error('[POST /api/leads]', res.status, msg, bodyText.slice(0, 1200));
        throw new Error(msg);
      }
      const sid = data.sessionId;
      if (!sid) throw new Error(fillCopy(c.invalidSessionNoId, { snippet: bodyText.slice(0, 200) }));
      const bound = reportEmail.trim().toLowerCase();
      setSessionLeadEmail(bound);
      setSessionId(sid);
      if (data.schoolReportCoverage === 'org_prepaid' || data.schoolReportCoverage === 'parent_pays') {
        setSchoolReportCoverage(String(data.schoolReportCoverage));
      }
      try {
        sessionStorage.setItem('crc_session_id', sid);
      } catch {
        /* ignore */
      }
      setPhase(PHASE.question);
    } catch (e) {
      const aborted = e?.name === 'AbortError' || e?.name === 'TimeoutError';
      const raw = String(e?.message || e || 'Unknown error');
      const net = /Failed to fetch|NetworkError|Load failed|ECONNREFUSED|ERR_CONNECTION|fetch/i.test(raw);
      const technical = net
        ? `${raw}\n\n${fillCopy(c.leadFetchNetworkHint, {
            base: API_BASE || '(same origin — CRA proxy to 127.0.0.1:8787)',
          })}`
        : raw;
      // eslint-disable-next-line no-console
      console.error('[POST /api/leads] failed', e);
      setIntroFieldError(
        aborted
          ? fillCopy(c.leadTimeoutHelp, { seconds: Math.round(leadFetchMs / 1000) })
          : technical,
      );
    } finally {
      setLeadBusy(false);
    }
  }, [
    name,
    reportEmail,
    leadPhone,
    leadClass,
    leadConsent,
    searchParams,
    assessmentGrade,
    publicConfig.funnelMode,
    publicConfig.databaseConfigured,
    leadDbCheck,
    leadDbDetail,
    assessmentLocale,
  ]);

  const choosePlan = useCallback(
    async (plan) => {
      if (!sessionId) return;
      const c = getAssessmentPageCopy(assessmentLocale);
      setUnlockVoucherMsg('');
      setPlanBusy(true);
      try {
        const res = await fetch(`${API_BASE}/api/sessions/${sessionId}/plan`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ planId: plan.id }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || c.couldNotSavePlan);
        setSelectedPlan(data.plan || plan);
        if (publicConfig.unlock === 'none') {
          const finalAnswers = pendingAnswers || answers;
          setPhase(PHASE.loading);
          void submitReport(finalAnswers, {});
          return;
        }
        setPhase(PHASE.unlock);
      } catch (e) {
        setUnlockVoucherMsg(String(e.message || e));
      } finally {
        setPlanBusy(false);
      }
    },
    [sessionId, assessmentLocale, publicConfig.unlock, pendingAnswers, answers, submitReport],
  );

  const validateUnlockVoucher = useCallback(async () => {
    const c = getAssessmentPageCopy(assessmentLocale);
    setUnlockVoucherMsg('');
    const code = unlockVoucher.trim();
    if (!code) {
      setUnlockVoucherMsg(c.enterCode);
      return false;
    }
    setUnlockBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/vouchers/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setUnlockVoucherMsg(data.message || data.error || c.invalidCode);
        return false;
      }
      setUnlockVoucherMsg(
        fillCopy(c.appliedVoucher, {
          pct: data.percentOff,
          suffix: data.label ? ` · ${data.label}` : '',
        }),
      );
      return true;
    } catch (e) {
      setUnlockVoucherMsg(String(e.message || e));
      return false;
    } finally {
      setUnlockBusy(false);
    }
  }, [unlockVoucher, assessmentLocale]);

  const startRazorpay = useCallback(async () => {
    const c = getAssessmentPageCopy(assessmentLocale);
    setPayBusy(true);
    setUnlockVoucherMsg('');
    try {
      await loadRazorpayScript();
      const paymentEmail =
        isLeadFunnelBrowserSession && sessionLeadEmail
          ? String(sessionLeadEmail).trim().toLowerCase()
          : reportEmail.trim().toLowerCase();
      const res = await fetch(`${API_BASE}/api/payments/razorpay-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim() || 'Student',
          reportEmail: paymentEmail,
          email: paymentEmail,
          ...(isLeadFunnelBrowserSession ? { sessionId } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || fillCopy(c.couldNotStartPayment, { status: res.status }));
      const key = data.keyId || publicConfig.razorpayKeyId;
      if (!key || !window.Razorpay) throw new Error(c.razorpayUnavailable);
      const options = {
        key,
        amount: data.amount,
        currency: data.currency || 'INR',
        order_id: data.orderId,
        name: 'ChooseRightCareer',
        description: c.razorpayDescription,
        prefill: {
          name: name.trim() || 'Student',
          email: paymentEmail,
        },
        theme: { color: '#f59e0b' },
        handler(response) {
          setRzpDone({
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature,
          });
          setUnlockVoucherMsg(getAssessmentPageCopy(assessmentLocale).paymentReceived);
        },
      };
      const rz = new window.Razorpay(options);
      rz.open();
    } catch (e) {
      const msg = String(e.message || e);
      if (/Could not load Razorpay checkout/i.test(msg)) setUnlockVoucherMsg(c.couldNotLoadCheckout);
      else setUnlockVoucherMsg(msg);
    } finally {
      setPayBusy(false);
    }
  }, [
    name,
    reportEmail,
    sessionLeadEmail,
    publicConfig.razorpayKeyId,
    sessionId,
    assessmentLocale,
    isLeadFunnelBrowserSession,
  ]);

  const continueFromUnlock = useCallback(async () => {
    const c = getAssessmentPageCopy(assessmentLocale);
    const finalAnswers = pendingAnswers || answers;
    setUnlockVoucherMsg('');

    if (
      (publicConfig.funnelMode === 'lead_plan_pay' || flowOverride === 'direct' || flowOverride === 'school') &&
      !sessionId
    ) {
      setUnlockVoucherMsg(c.sessionLost);
      return;
    }

    if (publicConfig.unlock === 'voucher') {
      const ok = await validateUnlockVoucher();
      if (!ok) return;
    }

    if (publicConfig.unlock === 'payment') {
      const vRes = unlockVoucher.trim()
        ? await fetch(`${API_BASE}/api/vouchers/validate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: unlockVoucher.trim() }),
          }).then((r) => r.json().then((d) => ({ ok: r.ok, d })))
        : { ok: false, d: {} };
      const freeByVoucher = vRes.ok && Number(vRes.d?.percentOff) >= 100;
      if (!freeByVoucher && !rzpDone) {
        setUnlockVoucherMsg(c.payOrCode);
        return;
      }
    }

    const boundLead =
      isLeadFunnelBrowserSession && sessionLeadEmail
        ? String(sessionLeadEmail).trim().toLowerCase()
        : '';
    const inbox = (boundLead || reportEmail.trim()).toLowerCase();
    const snap = {
      reportEmail: inbox,
      email: inbox,
      voucherCode: unlockVoucher.trim(),
      razorpayOrderId: rzpDone?.razorpayOrderId || '',
      razorpayPaymentId: rzpDone?.razorpayPaymentId || '',
      razorpaySignature: rzpDone?.razorpaySignature || '',
    };
    unlockSnapshotRef.current = snap;
    setPhase(PHASE.loading);
    void submitReport(finalAnswers, snap);
  }, [
    answers,
    pendingAnswers,
    publicConfig.unlock,
    publicConfig.funnelMode,
    sessionId,
    rzpDone,
    submitReport,
    reportEmail,
    sessionLeadEmail,
    unlockVoucher,
    validateUnlockVoucher,
    assessmentLocale,
    flowOverride,
    isLeadFunnelBrowserSession,
  ]);

  const backdropClass = useMemo(
    () => backdrops[(q?.level || 1) - 1] ?? backdrops[0],
    [q?.level],
  );
  /** Light sky / blue / teal shell to match the marketing site (hero). Question flow stays cinematic. */
  const brandChrome = useMemo(
    () =>
      [
        PHASE.boot,
        PHASE.intro,
        PHASE.plan,
        PHASE.unlock,
        PHASE.parent,
        PHASE.loading,
        PHASE.result,
        PHASE.sessionLinkError,
      ].includes(phase),
    [phase],
  );

  useEffect(() => {
    if (phase !== PHASE.parent) return;
    const ans = pendingAnswers || answers;
    if (!Array.isArray(ans) || ans.length !== 34) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/assessment/score`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ answers: ans }),
        });
        const d = await res.json().catch(() => ({}));
        if (!cancelled && d.profile) setParentPreviewProfile(d.profile);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, pendingAnswers, answers]);

  const submitParentPhone = useCallback(async () => {
    const digits = parentPhone.replace(/\D/g, '');
    if (digits.length < 10) {
      setParentErr('Enter a valid mobile number.');
      return;
    }
    setParentBusy(true);
    setParentErr('');
    setParentElapsedSeconds(0);
    parentWaitStartedAtRef.current = Date.now();
    if (parentElapsedIntervalRef.current) {
      clearInterval(parentElapsedIntervalRef.current);
      parentElapsedIntervalRef.current = null;
    }
    parentElapsedIntervalRef.current = setInterval(() => {
      const t0 = parentWaitStartedAtRef.current;
      if (!t0) return;
      setParentElapsedSeconds(Math.floor((Date.now() - t0) / 1000));
    }, 250);
    const tok = getSessionToken();
    const parentReportMs = Number(process.env.REACT_APP_REPORT_FETCH_TIMEOUT_MS || 360000);
    try {
      const res = await fetchWithTimeout(
        `${API_BASE}/api/users/add-parent`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(tok ? { 'X-Session-Token': tok } : {}),
          },
          body: JSON.stringify({
            sessionId,
            parentPhone: digits,
          }),
        },
        parentReportMs,
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
      setProfile(data.profile || null);
      setReportPrintAvailable(Boolean(data.reportPrintAvailable));
      setReportPreviewUrl(String(data.previewUrl || ''));
      const pn = data.parentNotify;
      setParentNotifyMeta(
        pn && typeof pn === 'object'
          ? { mode: String(pn.mode || ''), messageSid: pn.messageSid ? String(pn.messageSid) : '' }
          : null,
      );
      const nextDl = data.pdfDownload?.reportId && data.pdfDownload?.token ? data.pdfDownload : null;
      pdfDownloadRef.current = nextDl;
      setPdfDownload(nextDl);
      setPdfBase64('');
      setPdfWarning(
        data.pdfWarning ? friendlySurfaceErrorForLocale(String(data.pdfWarning), assessmentLocale) || String(data.pdfWarning) : '',
      );
      setReportSoftFail(false);
      if (
        resolveServerPdfDownloadEnabled(publicConfig) &&
        resolveAutoDownloadPdfAfterGenerate(publicConfig) &&
        nextDl
      ) {
        autoDownloadAndExitRef.current = true;
      }
      try {
        const sr = await fetchWithTimeout(`${API_BASE}/api/sessions/${encodeURIComponent(sessionId)}`, {}, 15000);
        if (sr.ok) {
          const sd = await sr.json().catch(() => ({}));
          if (sd.paymentStatus != null) setFunnelPaymentStatus(String(sd.paymentStatus).toLowerCase());
          const cov = String(sd.schoolReportCoverage || '').trim();
          if (cov === 'org_prepaid' || cov === 'parent_pays') setSchoolReportCoverage(cov);
        }
      } catch {
        /* ignore */
      }
      setPhase(PHASE.result);
    } catch (e) {
      setParentErr(
        friendlySurfaceErrorForLocale(String(e.message || e), assessmentLocale) ||
          String(e.message || e),
      );
    } finally {
      if (parentElapsedIntervalRef.current) {
        clearInterval(parentElapsedIntervalRef.current);
        parentElapsedIntervalRef.current = null;
      }
      parentWaitStartedAtRef.current = 0;
      setParentElapsedSeconds(null);
      setParentBusy(false);
    }
  }, [parentPhone, sessionId, assessmentLocale, publicConfig]);
  const hasStoredPdf = Boolean(pdfDownload?.reportId && pdfDownload?.token);
  const hasPdf = Boolean(pdfBase64 || hasStoredPdf);
  const serverPdfDownload = useMemo(() => resolveServerPdfDownloadEnabled(publicConfig), [publicConfig]);
  const autoDownloadPdfAfterGenerate = useMemo(
    () => resolveAutoDownloadPdfAfterGenerate(publicConfig),
    [publicConfig],
  );
  const suppressServerPdfUserAlerts = !serverPdfDownload && Boolean(profile) && !reportSoftFail;
  const resultActionBusy = flowOverride === 'school' ? parentBusy || reportBusy : reportBusy;

  const downloadStoredPdf = useCallback(async () => {
    const d = pdfDownloadRef.current || pdfDownload;
    if (!d?.reportId || !d?.token) return;
    const sessionTok = getSessionToken();
    const inbox = String(sessionLeadEmail || reportEmail || '')
      .trim()
      .toLowerCase();
    const res = await fetch(`${API_BASE}/api/reports/download`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionTok ? { 'X-Session-Token': sessionTok } : {}),
      },
      body: JSON.stringify({
        reportId: d.reportId,
        token: d.token,
        reportEmail: inbox || undefined,
        email: inbox || undefined,
        sessionToken: sessionTok || undefined,
        downloadContext: 'assessment',
      }),
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.error || `Download failed (${res.status})`);
    }
    const blob = await res.blob();
    downloadBlob(blob, defaultPdfFileName());
  }, [pdfDownload, sessionLeadEmail, reportEmail]);

  const handleDownloadPdf = useCallback(async () => {
    try {
      if (pdfBase64) {
        downloadPdfFromBase64(pdfBase64, defaultPdfFileName());
        return;
      }
      await downloadStoredPdf();
    } catch (e) {
      setError(
        friendlySurfaceErrorForLocale(String(e?.message || e), assessmentLocale) ||
          String(e?.message || e),
      );
    }
  }, [pdfBase64, downloadStoredPdf, assessmentLocale]);

  useEffect(() => {
    if (!serverPdfDownload || !autoDownloadPdfAfterGenerate) return;
    if (phase !== PHASE.result) return;
    if (!autoDownloadAndExitRef.current) return;
    if (!hasPdf) return;
    autoDownloadAndExitRef.current = false;
    let cancelled = false;
    void (async () => {
      setAutoFinishingReport(true);
      try {
        if (pdfBase64) {
          downloadPdfFromBase64(pdfBase64, defaultPdfFileName());
        } else {
          await downloadStoredPdf();
        }
      } catch (e) {
        if (!cancelled) {
          setReportSoftFail(true);
          setError(
            friendlySurfaceErrorForLocale(String(e?.message || e), assessmentLocale) ||
              String(e?.message || e),
          );
        }
      } finally {
        if (!cancelled) setAutoFinishingReport(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    phase,
    pdfBase64,
    pdfDownload,
    serverPdfDownload,
    autoDownloadPdfAfterGenerate,
    hasPdf,
    downloadStoredPdf,
    assessmentLocale,
  ]);

  const openClientPrintDialog = useCallback(async () => {
    if (flowOverride === 'school' && !schoolStudentCanPrintFullReport) return;
    setPrintSheetOpen(true);
    const tok = getSessionToken();
    let usedFullHtml = false;
    const c = getAssessmentPageCopy(assessmentLocale);
    try {
      /* Always try server HTML when we have session auth — do not rely on reportPrintAvailable alone (flag can be missing on some responses; DB rebuild still works). */
      if (sessionId && tok) {
        try {
          const res = await fetch(
            `${API_BASE}/api/assessment/report-print-html?sessionId=${encodeURIComponent(sessionId)}&sessionToken=${encodeURIComponent(tok)}`,
          );
          if (res.status === 402) {
            setError(c.schoolPrintRequiresPayment);
          } else if (res.ok) {
            const html = await res.text();
            if (isUsableReportPrintHtml(html) && printFullDocumentHtml(html)) {
              usedFullHtml = true;
            }
          }
        } catch {
          /* school: no snapshot fallback */
        }
      }
      void trackEvent('report_print_dialog_opened', {
        meta: {
          surface: 'assessment_result',
          flow: String(flowOverride || 'direct'),
          mode: usedFullHtml ? 'full_html' : flowOverride === 'school' ? 'full_html_failed' : 'snapshot_fallback',
        },
      });
      if (!usedFullHtml) {
        if (flowOverride === 'school') {
          setError(c.schoolPrintFullReportUnavailable);
        } else {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              window.print();
            });
          });
        }
      }
    } finally {
      window.setTimeout(() => setPrintSheetOpen(false), usedFullHtml ? 1600 : 750);
    }
  }, [flowOverride, sessionId, schoolStudentCanPrintFullReport, assessmentLocale]);

  useLayoutEffect(() => {
    if (location.pathname !== '/assessment') return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname, phase, qIndex]);

  return (
    <div
      className={`relative min-h-[100dvh] overflow-x-hidden ${brandChrome ? 'text-gray-900' : 'text-white'}`}
    >
      <PageSeo title={copy.seoTitle} description={copy.seoDesc} path="/assessment" />
      <GoogleAnalytics />

      {brandChrome ? (
        <>
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-sky-50 via-blue-50 to-teal-50 transition-all [transition-duration:900ms] ease-in-out"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute top-20 right-10 h-56 w-56 rounded-full bg-blue-200/35 blur-3xl sm:right-24 sm:h-72 sm:w-72"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute bottom-16 left-8 h-64 w-64 rounded-full bg-teal-200/35 blur-3xl sm:left-20 sm:h-80 sm:w-80"
            aria-hidden
          />
        </>
      ) : (
        <div
          className={`pointer-events-none absolute inset-0 transition-all [transition-duration:900ms] ease-in-out ${backdropClass}`}
          aria-hidden
        />
      )}
      <div
        className={
          brandChrome
            ? 'pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_90%_75%_at_50%_35%,rgba(255,255,255,0.65)_0%,transparent_58%)]'
            : 'pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_85%_70%_at_50%_42%,transparent_0%,rgba(0,0,0,0.35)_55%,rgba(0,0,0,0.82)_100%)]'
        }
        aria-hidden
      />
      <div
        className={`pointer-events-none absolute inset-0 mix-blend-overlay ${brandChrome ? 'opacity-[0.04]' : 'opacity-[0.07]'}`}
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
        aria-hidden
      />

      <header
        className={`fixed top-0 left-0 right-0 z-30 backdrop-blur-md ${
          brandChrome
            ? 'border-b border-blue-100/90 bg-white/85 shadow-sm shadow-blue-900/5'
            : 'border-b border-white/10 bg-black/40'
        }`}
      >
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2 px-4 py-2 sm:px-6 sm:py-2.5">
          <button
            type="button"
            className={`inline-flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium transition ${
              brandChrome
                ? 'text-blue-800 hover:bg-blue-50 hover:text-blue-900'
                : 'text-blue-100/90 hover:bg-white/10 hover:text-white'
            }`}
            onClick={() => openLeaveConfirm('home')}
          >
            <Home className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
            {copy.home}
          </button>
          <div className="flex flex-1 flex-wrap items-center justify-end gap-2 sm:gap-3">
            {publicConfig.organization?.name ? (
              <span
                className={`max-w-[10rem] truncate text-[11px] font-medium sm:max-w-xs ${
                  brandChrome ? 'text-teal-800' : 'text-teal-100/85'
                }`}
                title={publicConfig.organization.name}
              >
                {publicConfig.organization.name}
              </span>
            ) : null}
            <div
              className={`flex items-center gap-1 rounded-full p-0.5 text-[11px] font-medium ${
                brandChrome
                  ? 'border border-blue-200/80 bg-white/95 text-gray-700 shadow-sm'
                  : 'border border-white/15 bg-black/35 text-white/85'
              }`}
            >
              <button
                type="button"
                className={`rounded-full px-2.5 py-1 transition ${
                  assessmentLocale === 'en'
                    ? brandChrome
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-white/15 text-white'
                    : brandChrome
                      ? 'text-gray-500 hover:text-gray-900'
                      : 'text-white/60 hover:text-white'
                }`}
                onClick={() => {
                  setAssessmentLocale('en');
                  try {
                    window.localStorage.setItem('crc_assessment_lang', 'en');
                  } catch {
                    /* ignore */
                  }
                }}
              >
                EN
              </button>
              <button
                type="button"
                className={`rounded-full px-2.5 py-1 transition ${
                  assessmentLocale === 'hi'
                    ? brandChrome
                      ? 'bg-teal-600 text-white shadow-sm'
                      : 'bg-white/15 text-white'
                    : brandChrome
                      ? 'text-gray-500 hover:text-gray-900'
                      : 'text-white/60 hover:text-white'
                }`}
                onClick={() => {
                  setAssessmentLocale('hi');
                  try {
                    window.localStorage.setItem('crc_assessment_lang', 'hi');
                  } catch {
                    /* ignore */
                  }
                }}
              >
                हि
              </button>
            </div>
            {assessmentLocale === 'hi' && hiQuestionPackLooksUntranslated() ? (
              <p
                className={`max-w-[14rem] text-[9px] leading-snug sm:max-w-xs sm:text-[10px] ${
                  brandChrome ? 'text-amber-800' : 'text-amber-200/90'
                }`}
                title="Hindi translations for every question are still rolling out"
              >
                {copy.hiJsonHint}
              </p>
            ) : null}
            <span
              className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] ${
                brandChrome ? 'text-blue-700' : 'text-amber-200/90'
              }`}
            >
              <Clapperboard className="h-3.5 w-3.5" aria-hidden />
              {copy.careerDna}
            </span>
          </div>
        </div>
      </header>

      <div
        className={`fixed left-0 top-0 z-20 h-1 bg-gradient-to-r transition-[width] duration-500 ease-out ${
          brandChrome
            ? 'from-blue-600 via-teal-500 to-blue-500 shadow-[0_0_14px_rgba(37,99,235,0.35)]'
            : 'from-amber-400 via-teal-400 to-violet-500 shadow-[0_0_20px_rgba(45,212,191,0.5)]'
        }`}
        style={{ width: `${progressPct}%` }}
        aria-hidden
      />

      <main
        id="crc-assessment-main"
        className="relative z-10 mx-auto max-w-5xl px-4 pb-[max(5rem,env(safe-area-inset-bottom))] pt-[3.5rem] sm:px-6 sm:pb-24 sm:pt-[4.25rem]"
      >
        {phase === PHASE.boot && (
          <div className="flex flex-col items-center gap-3 pt-10 text-center sm:items-start sm:pt-14 sm:text-left">
            <Loader2
              className={`h-9 w-9 animate-spin sm:ml-1 ${brandChrome ? 'text-blue-600' : 'text-amber-200/90'}`}
            />
            <p className={`text-sm ${brandChrome ? 'text-gray-600' : 'text-white/65'}`}>{copy.bootLoading}</p>
          </div>
        )}

        {phase === PHASE.sessionLinkError && (
          <div className="mx-auto max-w-lg pt-8 sm:pt-12">
            <div className="overflow-hidden rounded-2xl border border-amber-100/90 bg-white/95 px-6 py-8 text-center shadow-xl shadow-amber-900/10 sm:px-8">
              <div className="mx-auto mb-5 flex h-28 w-28 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 text-amber-700">
                <svg viewBox="0 0 120 120" className="h-24 w-24" aria-hidden>
                  <circle cx="60" cy="60" r="44" fill="#fff7ed" stroke="#fdba74" strokeWidth="3" />
                  <circle cx="44" cy="48" r="6" fill="#9a3412" />
                  <circle cx="76" cy="48" r="6" fill="#9a3412" />
                  <path
                    d="M38 78 Q60 62 82 78"
                    fill="none"
                    stroke="#c2410c"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                  <path d="M28 22 L32 18 L36 26 Z" fill="#fcd34d" opacity="0.9" />
                  <path d="M88 20 L94 28 L86 30 Z" fill="#fcd34d" opacity="0.85" />
                </svg>
              </div>
              <h1 className="font-display text-lg font-semibold text-gray-900 sm:text-xl">{copy.sessionLinkInvalidTitle}</h1>
              <p className="mt-3 text-sm leading-relaxed text-gray-600">{copy.sessionLinkInvalidBody}</p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
                <Button asChild className="rounded-xl bg-blue-600 text-white hover:bg-blue-700">
                  <Link to="/start">{copy.sessionLinkInvalidCta}</Link>
                </Button>
                <Button asChild variant="outline" className="rounded-xl border-blue-200 text-gray-800 hover:bg-blue-50/80">
                  <Link to="/">{copy.sessionLinkInvalidHome}</Link>
                </Button>
              </div>
            </div>
          </div>
        )}

        {phase === PHASE.plan && (
          <div className="mx-auto max-w-5xl space-y-5 sm:space-y-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl md:text-3xl">{copy.planTitle}</h2>
              <p className="mt-2 text-sm text-gray-600">{copy.planSubtitle}</p>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {(publicConfig.plans || []).map((plan) => (
                <button
                  key={plan.id}
                  type="button"
                  disabled={planBusy}
                  onClick={() => void choosePlan(plan)}
                  className={`rounded-2xl border p-6 text-left text-sm transition disabled:opacity-60 ${
                    plan.popular
                      ? 'border-blue-300/90 bg-white shadow-lg shadow-blue-900/10 ring-2 ring-blue-400/35 hover:border-blue-400 text-gray-800'
                      : 'border-blue-100/90 bg-white/90 text-gray-700 shadow-sm hover:border-blue-200 hover:bg-white'
                  }`}
                >
                  {plan.badge ? (
                    <div className="mb-2 inline-flex items-center rounded-full border border-blue-200/80 bg-blue-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-800">
                      {plan.badge}
                    </div>
                  ) : null}
                  <div className="text-lg font-semibold text-gray-900">{plan.name}</div>
                  {plan.tagline ? <p className="mt-1 text-xs text-gray-500">{plan.tagline}</p> : null}
                  <div className="mt-2 text-2xl font-bold text-blue-700">
                    {publicConfig.currency}{' '}
                    {(Number(plan.pricePaise || 0) / 100).toFixed(0)}
                  </div>
                  {Array.isArray(plan.bullets) && plan.bullets.length ? (
                    <ul className="mt-3 space-y-1.5 text-xs leading-relaxed text-gray-600">
                      {plan.bullets.map((b) => (
                        <li key={b} className="flex gap-2">
                          <span className="text-teal-600">✓</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-3 text-xs leading-relaxed text-gray-600">{plan.description}</p>
                  )}
                  {plan.footer ? (
                    <p className="mt-4 border-t border-blue-100/80 pt-3 text-[11px] font-medium text-gray-500">
                      {plan.footer}
                    </p>
                  ) : null}
                </button>
              ))}
            </div>
            <p className="text-center text-xs text-gray-500">{copy.planFooter}</p>
            {unlockVoucherMsg ? (
              <p className="text-center text-sm text-amber-800">{unlockVoucherMsg}</p>
            ) : null}
            <div className="flex justify-center">
              <Button
                type="button"
                variant="ghost"
                className="text-gray-600 hover:bg-blue-50/80 hover:text-gray-900"
                onClick={restart}
              >
                {copy.cancel}
              </Button>
            </div>
          </div>
        )}

        {phase === PHASE.intro && (
          <div className="mx-auto max-w-lg scroll-mt-28 pt-1 sm:pt-2">
            <div className="overflow-hidden rounded-2xl border border-blue-100/90 bg-white/95 shadow-xl shadow-blue-900/10 backdrop-blur-sm">
              <div className="border-b border-blue-100/80 bg-gradient-to-b from-white to-sky-50/40 px-5 py-4 text-center sm:px-6 sm:py-5 sm:text-left">
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-200/80 bg-white/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-blue-900 shadow-sm">
                  <Sparkles className="h-3 w-3 text-blue-600" aria-hidden />
                  {assessmentIntro.kicker}
                </div>
                <h1 className="mt-3 font-display text-xl font-semibold leading-snug tracking-tight text-gray-900 sm:text-2xl">
                  {assessmentIntro.title}
                </h1>
                <p className="mx-auto mt-2 max-w-prose text-[13px] leading-relaxed text-gray-600 sm:mx-0 sm:text-sm">
                  {assessmentIntro.body}
                </p>
                <div className="mx-auto mt-3 flex max-w-md flex-wrap justify-center gap-2 sm:mx-0 sm:justify-start">
                  {assessmentIntro.bullets.map((b) => (
                    <span
                      key={b}
                      className="rounded-full border border-blue-100 bg-blue-50/80 px-2.5 py-1 text-[11px] leading-tight text-gray-700"
                    >
                      {b}
                    </span>
                  ))}
                </div>
                {publicConfig.funnelMode === 'lead_plan_pay' && sessionId ? (
                  <p className="mt-3 text-center text-[11px] text-teal-800 sm:text-left">{copy.sessionRestored}</p>
                ) : null}
              </div>

              <div className="space-y-4 bg-white/80 px-5 py-5 sm:px-6 sm:py-6">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">{copy.introYourDetails}</p>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label htmlFor="crc-name" className="text-[11px] font-medium text-gray-600">
                      {copy.introName}
                    </label>
                    <input
                      id="crc-name"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setIntroFieldError('');
                      }}
                      placeholder={copy.introNamePh}
                      autoComplete="name"
                      className="w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-[15px] text-gray-900 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200/60"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="crc-report-email" className="text-[11px] font-medium text-gray-600">
                      {copy.introEmail}
                    </label>
                    <input
                      id="crc-report-email"
                      type="email"
                      value={reportEmail}
                      readOnly={introEmailLocked}
                      aria-readonly={introEmailLocked || undefined}
                      onChange={(e) => {
                        if (introEmailLocked) return;
                        setReportEmail(e.target.value);
                        setIntroFieldError('');
                      }}
                      placeholder={copy.introEmailPh}
                      autoComplete="email"
                      inputMode="email"
                      className={`w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-[15px] text-gray-900 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200/60 ${
                        introEmailLocked ? 'cursor-not-allowed opacity-80' : ''
                      }`}
                    />
                    <p className="text-[11px] leading-relaxed text-gray-500">{copy.introEmailHelp}</p>
                  </div>

                  <label className="flex cursor-pointer items-start gap-3 text-left text-[12px] leading-snug text-gray-700">
                    <input
                      type="checkbox"
                      checked={leadConsent}
                      onChange={(e) => setLeadConsent(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500/30"
                    />
                    <span>{copy.introConsent}</span>
                  </label>

                  <div className="space-y-1.5">
                    <label htmlFor="crc-intro-phone" className="text-[11px] font-medium text-gray-600">
                      {copy.introPhone}{' '}
                      <span className="font-normal lowercase text-gray-400">{copy.introOptional}</span>
                    </label>
                    <input
                      id="crc-intro-phone"
                      value={leadPhone}
                      onChange={(e) => setLeadPhone(e.target.value)}
                      className="w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-[15px] text-gray-900 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200/60"
                      placeholder={copy.phonePlaceholder}
                      autoComplete="tel"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="crc-intro-class" className="text-[11px] font-medium text-gray-600">
                      {copy.introClass}{' '}
                      <span className="font-normal lowercase text-gray-400">{copy.introOptional}</span>
                    </label>
                    <input
                      id="crc-intro-class"
                      value={leadClass}
                      onChange={(e) => {
                        const v = e.target.value;
                        setLeadClass(v);
                        const g = parseAssessmentGradeFromText(v);
                        if (g) setAssessmentGrade(g);
                      }}
                      className="w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-[15px] text-gray-900 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200/60"
                      placeholder={copy.introClassPh}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-blue-100/80 bg-slate-50/80 px-5 py-4 sm:px-6">
                {needsLeadCreate && publicConfig.databaseConfigured && leadDbCheck === 'checking' ? (
                  <p className="mb-3 text-center text-xs text-gray-500 sm:text-left">{copy.checkingDb}</p>
                ) : null}
                {needsLeadCreate && publicConfig.databaseConfigured && leadDbCheck === 'error' ? (
                  <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-left text-xs leading-relaxed text-amber-950">
                    <p className="font-medium text-amber-900">{copy.dbUnreachableTitle}</p>
                    <p className="mt-1 text-[11px] text-amber-900/85">{leadDbDetail}</p>
                    <button
                      type="button"
                      className="mt-2 text-[11px] font-semibold text-teal-700 underline decoration-teal-400/60 underline-offset-2 hover:text-teal-900"
                      onClick={() => {
                        setLeadDbCheck('checking');
                        setLeadDbDetail('');
                        void (async () => {
                          try {
                            const res = await fetchWithTimeout(`${API_BASE}/api/health?db=1`, {}, 15000);
                            const data = await res.json().catch(() => ({}));
                            const ping = data.databasePing;
                            if (ping && ping.ok === true) {
                              setLeadDbCheck('ok');
                              setLeadDbDetail('');
                            } else if (ping && ping.ok === false) {
                              setLeadDbCheck('error');
                              setLeadDbDetail(formatDatabasePingDetail(ping, copy.dbCheckFailed));
                            } else {
                              const c = copy;
                              setLeadDbCheck('error');
                              setLeadDbDetail(
                                ping
                                  ? formatDatabasePingDetail(ping, c.dbCheckFailed)
                                  : `${c.apiVerifyDbFail} ${c.dbCheckFailed}`.trim(),
                              );
                            }
                          } catch (e) {
                            setLeadDbCheck('error');
                            setLeadDbDetail(
                              friendlySurfaceErrorForLocale(String(e?.message || e), assessmentLocale) ||
                                copy.retryFailed,
                            );
                          }
                        })();
                      }}
                    >
                      {copy.dbRetry}
                    </button>
                  </div>
                ) : null}
                {introFieldError ? (
                  <p className="mb-3 whitespace-pre-wrap break-words text-center text-xs text-amber-800 sm:text-left sm:text-sm">
                    {introFieldError}
                  </p>
                ) : null}
                <div className="flex justify-center sm:justify-end">
                  <Button
                    size="lg"
                    disabled={
                      needsLeadCreate &&
                      (leadBusy ||
                        (publicConfig.databaseConfigured && leadDbCheck !== 'ok'))
                    }
                    className="min-w-[11rem] rounded-full border-2 border-transparent bg-gradient-to-r from-blue-600 to-blue-700 px-7 text-white shadow-lg shadow-blue-900/15 hover:from-blue-700 hover:to-blue-800 disabled:opacity-70"
                    onClick={() => {
                      setIntroFieldError('');
                      if (!name.trim()) {
                        setIntroFieldError(copy.errName);
                        return;
                      }
                      const req = publicConfig.requireReportEmail !== false;
                      const pe = validateReportEmailClient(reportEmail, req, assessmentLocale);
                      if (pe) {
                        setIntroFieldError(pe);
                        return;
                      }
                      if (!leadConsent) {
                        setIntroFieldError(copy.errConsent);
                        return;
                      }
                      if (needsLeadCreate) {
                        void submitLead();
                        return;
                      }
                      setPhase(PHASE.question);
                    }}
                  >
                    {needsLeadCreate && leadBusy ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" />
                        {copy.saving}
                      </>
                    ) : needsLeadCreate && publicConfig.databaseConfigured && leadDbCheck === 'checking' ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" />
                        {copy.verifyingDb}
                      </>
                    ) : (
                      <>
                        {assessmentIntro.cta}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {phase === PHASE.unlock && (
          <div className="mx-auto max-w-lg space-y-5 rounded-2xl border border-blue-100/90 bg-white/95 p-5 text-center shadow-xl shadow-blue-900/10 backdrop-blur-sm sm:space-y-6 sm:p-7 md:p-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-blue-200/80 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-900">
              <Sparkles className="h-3.5 w-3.5 text-blue-600" />
              {copy.unlockBadge}
            </div>
            <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">{copy.unlockTitle}</h2>
            <p className="text-sm text-gray-600">
              {publicConfig.unlock === 'voucher' ? copy.unlockBodyVoucher : copy.unlockBodyPayment}
            </p>

            <div className="rounded-xl border border-blue-100/90 bg-sky-50/50 px-4 py-3 text-left text-sm text-gray-800">
              <span className="text-xs uppercase tracking-wide text-gray-500">{copy.reportEmail}</span>
              <div className="mt-1 font-medium text-gray-900">
                {(sessionLeadEmail || reportEmail).trim() || '—'}
              </div>
              <p className="mt-2 text-xs text-gray-500">{copy.changeEmailHint}</p>
            </div>

            <div className="space-y-2 text-left">
              <label htmlFor="crc-unlock-voucher" className="text-xs uppercase tracking-wide text-gray-600">
                {copy.voucherCode}
                {publicConfig.unlock === 'payment' ? ` ${copy.voucher100note}` : ''}
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  id="crc-unlock-voucher"
                  value={unlockVoucher}
                  onChange={(e) => {
                    setUnlockVoucher(e.target.value);
                    setUnlockVoucherMsg('');
                  }}
                  placeholder={copy.voucherCodePlaceholder}
                  className="w-full flex-1 rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-900 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200/60"
                />
                {publicConfig.unlock === 'voucher' ? (
                  <Button
                    type="button"
                    variant="secondary"
                    className="shrink-0 rounded-xl"
                    disabled={unlockBusy}
                    onClick={() => void validateUnlockVoucher()}
                  >
                    {unlockBusy ? copy.verifyBusy : copy.verify}
                  </Button>
                ) : null}
              </div>
              {publicConfig.unlock === 'payment' ? <p className="text-xs text-gray-500">{copy.sponsorHint}</p> : null}
            </div>

            {publicConfig.unlock === 'payment' ? (
              <div className="space-y-3">
                <div className="text-sm text-gray-700">
                  {copy.reportLine}{' '}
                  <span className="font-medium text-gray-900">
                    {publicConfig.currency}{' '}
                    {(
                      Number(selectedPlan?.pricePaise ?? publicConfig.reportPricePaise ?? 129900) / 100
                    ).toFixed(0)}
                  </span>
                  {selectedPlan?.name ? (
                    <span className="block text-xs text-gray-500">
                      {copy.planLine} {selectedPlan.name}
                    </span>
                  ) : null}
                </div>
                <Button
                  type="button"
                  className="w-full rounded-full border border-gray-200 bg-white text-gray-900 hover:bg-gray-50"
                  disabled={payBusy || !publicConfig.razorpayKeyId}
                  onClick={() => void startRazorpay()}
                >
                  {payBusy ? copy.openingCheckout : copy.payRazorpay}
                </Button>
                {!publicConfig.razorpayKeyId ? <p className="text-xs text-amber-800">{copy.razorpayInactive}</p> : null}
              </div>
            ) : null}

            {unlockVoucherMsg ? (
              <p
                className={`text-sm ${
                  /Applied:|लागू:|Payment received|भुगतान मिल गया/i.test(unlockVoucherMsg)
                    ? 'text-emerald-700'
                    : 'text-amber-800'
                }`}
              >
                {unlockVoucherMsg}
              </p>
            ) : null}

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Button
                type="button"
                size="lg"
                className="rounded-full border-2 border-transparent bg-gradient-to-r from-blue-600 to-teal-600 px-8 text-white shadow-md hover:from-blue-700 hover:to-teal-700"
                onClick={() => void continueFromUnlock()}
              >
                {copy.generateReport}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="text-gray-600 hover:bg-blue-50/80 hover:text-gray-900"
                onClick={() => openLeaveConfirm('restart')}
              >
                {copy.cancel}
              </Button>
            </div>
          </div>
        )}

        {(phase === PHASE.question || phase === PHASE.bridge) && (
          <div
            key={phase === PHASE.bridge ? 'bridge' : `q-${q.id}`}
            className={`flex flex-col ${reducedMotion ? '' : 'animate-in fade-in slide-in-from-bottom-2 duration-300'}`}
          >
            {/* HUD: compact; stars scroll horizontally on narrow screens */}
            <div className="mb-4 flex flex-col items-center gap-2 sm:mb-5 sm:gap-2.5">
              <div className="flex flex-wrap items-center justify-center gap-2">
                <div className="flex items-center gap-1.5 rounded-full border border-white/15 bg-black/30 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-teal-200/95 backdrop-blur-sm sm:px-3.5 sm:text-[11px] sm:tracking-[0.2em]">
                  <Trophy className="h-3 w-3 text-amber-300 sm:h-3.5 sm:w-3.5" aria-hidden />
                  {phase === PHASE.bridge ? copy.checkpoint : fillCopy(copy.questionN, { n: q.id })}
                </div>
                <span className="tabular-nums text-[10px] font-bold uppercase tracking-[0.16em] text-amber-200/80 sm:text-[11px]">
                  {Math.round(progressPct)}%
                </span>
              </div>
              <div
                className="flex max-w-full flex-nowrap justify-center gap-0.5 overflow-x-auto overflow-y-hidden px-1 pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] sm:max-w-[min(100%,36rem)] sm:flex-wrap sm:justify-center sm:gap-1 sm:overflow-visible sm:px-0 [&::-webkit-scrollbar]:hidden"
                aria-hidden
              >
                {questions.map((qq, i) => {
                  const done = i < qIndex || (i === qIndex && phase === PHASE.bridge);
                  const active = i === qIndex && phase === PHASE.question;
                  return (
                    <Star
                      key={qq.id}
                      title={fillCopy(copy.questionStarTitle, { n: qq.id })}
                      className={`h-3 w-3 shrink-0 cursor-default transition-all duration-300 hover:scale-110 hover:drop-shadow-[0_0_8px_rgba(251,191,36,0.4)] sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 ${
                        done
                          ? 'fill-amber-400 text-amber-300 scale-100 drop-shadow-[0_0_6px_rgba(251,191,36,0.55)]'
                          : active
                            ? 'fill-amber-200/90 text-amber-100 scale-110 animate-pulse'
                            : 'fill-white/10 text-white/20'
                      }`}
                    />
                  );
                })}
              </div>
              <p className="max-w-md px-1 text-center text-[10px] font-medium uppercase leading-snug tracking-[0.1em] text-white/40 sm:text-[11px] sm:tracking-[0.12em]">
                {copy.takeYourTime}
              </p>
            </div>

            {phase === PHASE.bridge ? (
              <div className="mx-auto w-full max-w-2xl space-y-5 pb-4 sm:space-y-6 sm:pb-6">
                <div className="text-center">
                  <p className="mb-1.5 font-mono text-[9px] font-semibold uppercase tracking-[0.24em] text-teal-300/75 sm:mb-2 sm:text-[10px] sm:tracking-[0.28em]">
                    {fillCopy(copy.levelTransitionLabel, { level: q.level })}
                  </p>
                  <p className="font-display text-lg font-extrabold leading-snug tracking-tight text-white drop-shadow-md sm:text-2xl md:text-3xl">
                    {bridgeText}
                  </p>
                </div>
                <div className="flex justify-center">
                  <Button
                    type="button"
                    size="lg"
                    onClick={continueAfterBridge}
                    className="group relative h-12 min-w-[11rem] overflow-hidden rounded-xl border-2 border-teal-400/50 bg-gradient-to-r from-teal-500 via-emerald-500 to-blue-600 text-sm font-black tracking-wide text-white shadow-[0_0_28px_rgba(20,184,166,0.35)] transition hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(59,130,246,0.35)] sm:h-14 sm:min-w-[12rem] sm:rounded-2xl sm:text-base"
                  >
                    <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition duration-700 group-hover:translate-x-full" />
                    <span className="relative inline-flex items-center gap-2">
                      {copy.continue}
                      <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden />
                    </span>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mx-auto w-full max-w-2xl space-y-4 pb-4 sm:space-y-5 sm:pb-6">
                <div className="text-center">
                  <p className="mb-1.5 font-mono text-[9px] font-semibold uppercase tracking-[0.24em] text-teal-300/75 sm:mb-2 sm:text-[10px] sm:tracking-[0.28em]">
                    {fillCopy(copy.levelDiscoveryLabel, { level: q.level })}
                  </p>
                  <h2 className="font-display text-lg font-extrabold leading-snug tracking-tight text-white drop-shadow-md sm:text-2xl md:text-3xl">
                    {q.text}
                  </h2>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                  {q.options.map((o, idx) => (
                    <button
                      key={o.letter}
                      type="button"
                      onClick={() => pick(o.letter)}
                      className="group relative overflow-hidden rounded-xl border border-white/14 bg-gradient-to-b from-white/[0.08] to-white/[0.03] p-4 text-left shadow-md shadow-black/20 transition-all duration-200 hover:-translate-y-0.5 hover:border-teal-300/40 hover:from-white/[0.12] hover:shadow-teal-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/35 sm:rounded-2xl sm:p-5"
                    >
                      <span
                        className={`absolute left-0 top-0 h-full w-1.5 ${
                          idx % 2 === 0
                            ? 'bg-gradient-to-b from-blue-400 to-cyan-400'
                            : 'bg-gradient-to-b from-rose-400 to-amber-400'
                        }`}
                        aria-hidden
                      />
                      <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-lg bg-black/40 text-sm font-black text-white ring-1 ring-white/20 sm:right-4 sm:top-4 sm:h-9 sm:w-9 sm:rounded-xl sm:text-base">
                        {String(o.letter).toUpperCase()}
                      </span>
                      <p className="pr-11 font-display text-sm font-bold leading-snug text-white sm:pr-12 sm:text-base md:text-lg">
                        {o.label}
                      </p>
                      <p className="mt-3 font-mono text-[9px] font-bold uppercase tracking-wider text-teal-200/70 opacity-0 transition group-hover:opacity-100 sm:mt-4 sm:text-[10px]">
                        {copy.tapToChoose}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {phase === PHASE.parent && (
          <div className="mx-auto max-w-lg space-y-5 rounded-2xl border border-blue-100/90 bg-white/95 p-5 text-center shadow-xl sm:p-8">
            {parentBusy ? (
              <div className="space-y-5 py-2">
                <Loader2 className="mx-auto h-11 w-11 animate-spin text-blue-600" aria-hidden />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">{copy.parentGenerateTitle}</h2>
                  <p className="mt-2 text-sm text-gray-600">{copy.parentGenerateCountdownHint}</p>
                </div>
                {parentElapsedSeconds != null ? (
                  <div
                    className="mx-auto max-w-xs rounded-2xl border border-blue-100 bg-gradient-to-b from-sky-50/90 to-white px-6 py-6 shadow-inner"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Elapsed</p>
                    <p className="mt-2 font-mono text-5xl font-bold tabular-nums tracking-tight text-blue-700 sm:text-6xl">
                      {formatElapsedMmSs(parentElapsedSeconds)}
                    </p>
                    {parentElapsedSeconds >= 45 ? (
                      <p className="mt-3 text-xs leading-relaxed text-slate-600">{copy.parentGenerateAlmost}</p>
                    ) : null}
                  </div>
                ) : null}
                <p className="text-xs font-medium text-slate-500">{copy.parentSubmitBusy}</p>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">{copy.parentStepTitle}</h2>
                <p className="text-sm text-gray-600">{copy.parentStepBody}</p>
                {parentPreviewProfile ? (
                  <div className="rounded-xl border border-blue-100/80 bg-sky-50/50 px-4 py-3 text-left text-sm text-gray-800">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Your top strengths</p>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      {(() => {
                        const tl = {
                          analytical: copy.traitAnalytical,
                          creative: copy.traitCreative,
                          structured: copy.traitStructured,
                          social: copy.traitSocial,
                          practical: copy.traitPractical,
                          risk: copy.traitRisk,
                        };
                        return topTraitKeysFromProfile(parentPreviewProfile)
                          .map((k) => `${tl[k] || k} ${parentPreviewProfile[k]}%`)
                          .join(' · ');
                      })()}
                    </p>
                    <p className="mt-2 text-xs text-gray-600">
                      You lean most into your top two traits — your full report breaks down what this means for careers.
                    </p>
                  </div>
                ) : null}
                <div className="text-left">
                  <label className="text-xs font-medium text-gray-600" htmlFor="crc-parent-wa">
                    {copy.parentPhoneLabel}
                  </label>
                  <input
                    id="crc-parent-wa"
                    type="tel"
                    className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-[15px] outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200/60"
                    placeholder="10-digit mobile"
                    value={parentPhone}
                    onChange={(e) => {
                      setParentPhone(e.target.value);
                      setParentErr('');
                    }}
                    autoComplete="tel"
                  />
                </div>
                {parentErr ? (
                  <p className="text-sm text-amber-800">
                    {friendlySurfaceErrorForLocale(parentErr, assessmentLocale) || parentErr}
                  </p>
                ) : null}
                <Button
                  type="button"
                  size="lg"
                  disabled={parentBusy}
                  className="w-full min-w-[240px] rounded-full border-2 border-transparent bg-gradient-to-r from-blue-600 to-teal-600 px-8 text-white shadow-lg shadow-blue-900/20 hover:from-blue-700 hover:to-teal-700 disabled:opacity-70"
                  onClick={() => void submitParentPhone()}
                >
                  <Printer className="mr-2 h-5 w-5 shrink-0" aria-hidden />
                  {copy.parentSubmitPreparePrint}
                </Button>
              </>
            )}
          </div>
        )}

        {phase === PHASE.loading && (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-center sm:py-28">
            <Loader2 className={`h-10 w-10 animate-spin ${brandChrome ? 'text-blue-600' : 'text-amber-200'}`} />
            <p className={`text-base sm:text-lg ${brandChrome ? 'text-gray-800' : 'text-white/80'}`}>
              {fillCopy(copy.loadingReadLine, { namePart: name.trim() ? `, ${name.trim()}` : '' })}
            </p>
            <p className={`max-w-sm px-2 text-sm ${brandChrome ? 'text-gray-600' : 'text-white/50'}`}>
              {copy.loadingSub}
            </p>
          </div>
        )}

        {phase === PHASE.result && (
          <div className="crc-report-print-root space-y-6 sm:space-y-8">
            <div className="crc-print-preserve-bg crc-print-avoid-break rounded-2xl border border-blue-100/90 bg-white/95 p-5 text-center shadow-xl shadow-blue-900/10 backdrop-blur-sm sm:p-7 md:p-8">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-blue-100 to-teal-100 text-blue-700 ring-2 ring-blue-200/60">
                <Sparkles className="h-7 w-7" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl md:text-3xl">{copy.resultTitle}</h2>
              <p className="mx-auto mt-3 max-w-md text-gray-600">
                {flowOverride === 'school'
                  ? serverPdfDownload
                    ? copy.schoolResultBody
                    : schoolStudentCanPrintFullReport
                      ? copy.schoolResultBodyPrintFirst
                      : copy.schoolResultBody
                  : serverPdfDownload
                    ? copy.resultBody
                    : copy.resultBodyPrintFirst}
                {error && !suppressServerPdfUserAlerts ? copy.resultGlitch : null}
              </p>
              <p className="mx-auto mt-4 max-w-lg text-xs text-gray-500">
                {flowOverride === 'school'
                  ? serverPdfDownload
                    ? copy.schoolResultNote
                    : schoolStudentCanPrintFullReport
                      ? copy.schoolResultNotePrintFirst
                      : copy.schoolResultNote
                  : serverPdfDownload
                    ? copy.resultNote
                    : copy.resultNotePrintFirst}
              </p>
            </div>

            {flowOverride === 'school' ? (
              <div className="crc-no-print mx-auto max-w-md space-y-3 text-center">
                <p className="text-sm text-gray-600">{copy.schoolParentSmsNote}</p>
                {parentNotifyMeta?.mode ? (
                  <p className="rounded-lg border border-slate-200 bg-slate-50/90 px-3 py-2 text-xs leading-relaxed text-slate-700">
                    {parentNotifyMeta.mode === 'twilio_sms' || parentNotifyMeta.mode === 'twilio_whatsapp'
                      ? fillCopy(copy.parentNotifyTwilioOk, {
                          sid: parentNotifyMeta.messageSid || '—',
                        })
                      : fillCopy(copy.parentNotifyMock, { mode: parentNotifyMeta.mode })}
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="crc-no-print flex flex-col items-center gap-3">
              <div className="flex w-full max-w-md flex-col items-stretch gap-2 sm:flex-row sm:justify-center">
                {serverPdfDownload && hasPdf ? (
                  <Button
                    type="button"
                    size="lg"
                    disabled={resultActionBusy}
                    className="min-w-[200px] rounded-full border-2 border-transparent bg-gradient-to-r from-blue-600 to-teal-600 px-6 text-white shadow-lg shadow-blue-900/20 hover:from-blue-700 hover:to-teal-700 disabled:opacity-70 sm:flex-1"
                    onClick={() => void handleDownloadPdf()}
                  >
                    <Download className="mr-2 h-5 w-5 shrink-0" aria-hidden />
                    {copy.downloadPdf}
                  </Button>
                ) : null}
                {flowOverride !== 'school' || schoolStudentCanPrintFullReport ? (
                  <Button
                    type="button"
                    size="lg"
                    disabled={resultActionBusy}
                    className={
                      serverPdfDownload && hasPdf
                        ? 'min-w-[200px] rounded-full border-2 border-gray-300 bg-white px-6 text-gray-900 shadow-sm hover:bg-gray-50 disabled:opacity-70 sm:flex-1'
                        : 'min-w-[240px] rounded-full border-2 border-transparent bg-gradient-to-r from-blue-600 to-teal-600 px-8 text-white shadow-lg shadow-blue-900/20 hover:from-blue-700 hover:to-teal-700 disabled:opacity-70'
                    }
                    variant={serverPdfDownload && hasPdf ? 'outline' : 'default'}
                    onClick={() => void openClientPrintDialog()}
                  >
                    {resultActionBusy ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 shrink-0 animate-spin" />
                        {copy.preparingPdf}
                      </>
                    ) : (
                      <>
                        <Printer className="mr-2 h-5 w-5 shrink-0" aria-hidden />
                        {copy.printSavePdf}
                      </>
                    )}
                  </Button>
                ) : null}
              </div>
              {flowOverride === 'school' && !schoolStudentCanPrintFullReport ? (
                <p className="max-w-md text-center text-xs text-gray-500">{copy.schoolPrintAfterParentPaysHint}</p>
              ) : (
                <p className="max-w-md text-center text-xs text-gray-500">{copy.printSavePdfHint}</p>
              )}
              {resultActionBusy ? (
                <p className="max-w-md text-center text-xs text-gray-500">{copy.preparingHint}</p>
              ) : null}
              {!reportPrintAvailable &&
              !hasPdf &&
              !resultActionBusy &&
              (flowOverride !== 'school' || schoolStudentCanPrintFullReport) ? (
                <>
                  <p className="max-w-md text-center text-xs text-gray-500">{copy.notReadyHint}</p>
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-sm font-medium text-blue-700 underline-offset-2 hover:text-blue-800"
                    onClick={() =>
                      void submitReport(lastAnswersRef.current || pendingAnswers || answers, {
                        stayOnResult: true,
                      })
                    }
                  >
                    {copy.reportRetryPrepareShort}
                  </Button>
                </>
              ) : null}
            </div>

            {(printSheetOpen || autoFinishingReport) && (
              <div
                className="crc-no-print fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-[2px]"
                role="status"
                aria-live="polite"
                aria-busy="true"
              >
                <div className="max-w-sm rounded-2xl border border-white/25 bg-white p-6 text-center shadow-2xl">
                  <Loader2 className="mx-auto h-10 w-10 animate-spin text-blue-600" />
                  <p className="mt-4 font-semibold text-gray-900">
                    {autoFinishingReport ? copy.preparingPdf : copy.printPreparingTitle}
                  </p>
                  <p className="mt-2 text-sm text-gray-600">
                    {autoFinishingReport ? copy.autoDownloadHint : copy.printPreparingBody}
                  </p>
                </div>
              </div>
            )}

            {error && !suppressServerPdfUserAlerts ? (
              <div className="crc-no-print rounded-xl border border-red-200 bg-red-50/95 p-5 text-sm text-red-950 shadow-sm">
                <p className="font-semibold text-red-900">{copy.errStepTitle}</p>
                <p className="mt-2 leading-relaxed text-red-800/95">
                  {friendlySurfaceErrorForLocale(error, assessmentLocale) || error}
                </p>
                <p className="mt-3 text-xs text-red-700/90">{copy.errStepHint}</p>
              </div>
            ) : null}

            {pdfWarning && !error && !suppressServerPdfUserAlerts ? (
              <div className="crc-no-print rounded-xl border border-amber-200 bg-amber-50/95 p-5 text-sm text-amber-950 shadow-sm">
                <p className="font-semibold text-amber-900">{copy.pdfRetryTitle}</p>
                <p className="mt-2 leading-relaxed text-amber-900/90">
                  {friendlySurfaceErrorForLocale(pdfWarning, assessmentLocale) || pdfWarning}
                </p>
                <p className="mt-3 text-xs text-amber-800/90">{copy.pdfRetryHint}</p>
              </div>
            ) : null}

            {profile ? (
              <div className="crc-print-preserve-bg crc-print-avoid-break rounded-xl border border-blue-100/90 bg-gradient-to-br from-white via-sky-50/40 to-teal-50/30 p-6 shadow-md shadow-blue-900/5 ring-1 ring-blue-100/50">
                <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <Brain className="h-5 w-5 text-blue-600" />
                  {copy.snapshotTitle}
                </div>
                <p className="mb-4 text-xs text-gray-600">{copy.snapshotBody}</p>
                <div className="grid gap-3 text-sm text-gray-800 sm:grid-cols-2">
                  <div className="rounded-lg border border-blue-100/80 bg-white/90 px-3 py-2 shadow-sm">
                    {copy.traitAnalytical} {profile.analytical}%
                  </div>
                  <div className="rounded-lg border border-blue-100/80 bg-white/90 px-3 py-2 shadow-sm">
                    {copy.traitCreative} {profile.creative}%
                  </div>
                  <div className="rounded-lg border border-blue-100/80 bg-white/90 px-3 py-2 shadow-sm">
                    {copy.traitStructured} {profile.structured}%
                  </div>
                  <div className="rounded-lg border border-blue-100/80 bg-white/90 px-3 py-2 shadow-sm">
                    {copy.traitSocial} {profile.social}%
                  </div>
                  <div className="rounded-lg border border-blue-100/80 bg-white/90 px-3 py-2 shadow-sm">
                    {copy.traitPractical} {profile.practical}%
                  </div>
                  <div className="rounded-lg border border-blue-100/80 bg-white/90 px-3 py-2 shadow-sm">
                    {copy.traitRisk} {profile.risk}%
                  </div>
                </div>
              </div>
            ) : null}

            <div className="crc-no-print flex flex-wrap justify-center gap-3">
              <Button
                variant="secondary"
                className="rounded-full border border-gray-200 bg-white text-gray-800 hover:bg-gray-50"
                onClick={restart}
              >
                {copy.startAgain}
              </Button>
              <Button
                asChild
                className="rounded-full border border-blue-200 bg-blue-50 text-blue-900 hover:bg-blue-100"
              >
                <Link to="/">{copy.backHome}</Link>
              </Button>
            </div>
          </div>
        )}
      </main>

      {phase === PHASE.question && qIndex > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-black/40 px-3 py-2 backdrop-blur-md sm:p-4">
          <div className="mx-auto flex max-w-5xl justify-start px-4 sm:px-8">
            <Button
              variant="ghost"
              size="sm"
              className="text-white/70 hover:text-white"
              onClick={() => {
                setAnswers((prev) => {
                  const n = [...prev];
                  n[qIndex] = '';
                  return n;
                });
                setQIndex((i) => Math.max(0, i - 1));
              }}
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              {copy.previous}
            </Button>
          </div>
        </div>
      )}

      {phase === PHASE.question && qIndex === 0 && shouldConfirmLeaveAssessment && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-black/50 px-3 py-2.5 backdrop-blur-md sm:p-3">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2 px-4 sm:px-8">
            <span className="text-[11px] font-medium text-white/75 sm:text-xs">{copy.leaveAssessmentFooterQ1}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-amber-200/95 hover:bg-white/10 hover:text-amber-50"
              onClick={() => openLeaveConfirm('home')}
            >
              {copy.leaveAssessmentLeave}
            </Button>
          </div>
        </div>
      )}

      <AlertDialog
        open={leaveDialogOpen}
        onOpenChange={(open) => {
          setLeaveDialogOpen(open);
          if (!open) setLeaveAction(null);
        }}
      >
        <AlertDialogContent className="border-blue-100 bg-white text-gray-900 sm:rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{copy.leaveAssessmentTitle}</AlertDialogTitle>
            <AlertDialogDescription className="text-left text-sm leading-relaxed text-gray-600">
              {flowOverride === 'school' ? copy.leaveAssessmentBodySchool : copy.leaveAssessmentBodyDirectPaid}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl border-blue-200">{copy.leaveAssessmentStay}</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-xl bg-amber-600 text-white hover:bg-amber-700"
              onClick={() => confirmLeaveAssessment()}
            >
              {copy.leaveAssessmentLeave}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
